import { formatOrgNumber, type AdminProviderListItem, type ProviderStatus } from '@hemmafixa/shared';
import {
  Avatar,
  DataTable,
  EmptyState,
  MasterDetail,
  PageHeader,
  Pagination,
  SearchField,
  StatusBadge,
  UnderlineTabs,
  type ColumnDef,
} from '@hemmafixa/ui';
import { BadgeCheck, MousePointerClick } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { fill, useI18n } from '@/i18n/context';
import { useCategoryLabel } from '../shared/format';
import { useCounties } from '../shared/catalogue';
import { DetailPanel, FullPageLink, SelectableCard } from '../shared/master-detail-parts';
import { QueryView } from '../shared/query-view';
import { useStatuses } from '../shared/statuses';
import { useSelectedParam, useShownSelection } from '../shared/use-selected-param';
import { useAdminProviders, useProviderCounts } from './api';
import { AdminProviderDetail } from './provider-page';

type Filter = 'all' | ProviderStatus;
const FILTERS: Filter[] = ['all', 'pending', 'onboarding', 'verified', 'rejected'];
const PAGE_SIZE = 20;

/** The verification queue: profiles waiting for a decision first, oldest submission first (P2-10). */
export function ProvidersPage() {
  const { t, relative } = useI18n();
  const statuses = useStatuses();
  const [picked, setSelected] = useSelectedParam();
  const categoryLabel = useCategoryLabel();
  const counties = useCounties();
  const counts = useProviderCounts();
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('pending');
  const [page, setPage] = useState(1);
  const p = t.admin.providers;

  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const query = useAdminProviders({
    status: filter === 'all' ? undefined : filter,
    q: searchQuery || undefined,
    page,
    pageSize: PAGE_SIZE,
  });
  const selected = useShownSelection(
    picked,
    query.data?.items.map((item) => item.id),
  );

  const countyName = useMemo(() => {
    const names = new Map((counties.data ?? []).map((county) => [county.code, county.name]));
    return (code: string) => names.get(code) ?? code;
  }, [counties.data]);

  const columns = useMemo<ColumnDef<AdminProviderListItem, unknown>[]>(
    () => [
      {
        accessorKey: 'companyName',
        header: p.columns.company,
        cell: ({ row }) => (
          <span className="flex items-center gap-2.5">
            <Avatar seed={row.original.id} name={row.original.companyName ?? ''} kind="company" size="sm" />
            {row.original.companyName ?? <span className="text-muted-foreground">{p.noCompanyName}</span>}
          </span>
        ),
      },
      {
        accessorKey: 'orgNumber',
        header: p.columns.orgNumber,
        cell: ({ row }) => (row.original.orgNumber ? formatOrgNumber(row.original.orgNumber) : ''),
      },
      { accessorKey: 'contactName', header: p.columns.contact },
      {
        accessorKey: 'categorySlugs',
        header: p.columns.categories,
        enableSorting: false,
        cell: ({ row }) => row.original.categorySlugs.map(categoryLabel).join(', '),
      },
      {
        accessorKey: 'countyCodes',
        header: p.columns.counties,
        enableSorting: false,
        cell: ({ row }) => row.original.countyCodes.map(countyName).join(', '),
      },
      {
        accessorKey: 'status',
        header: p.columns.status,
        cell: ({ row }) => <StatusBadge status={row.original.status} statuses={statuses.provider} />,
      },
      {
        accessorKey: 'submittedAt',
        header: p.columns.submitted,
        cell: ({ row }) => (row.original.submittedAt ? relative(row.original.submittedAt) : ''),
      },
    ],
    [p, relative, statuses, categoryLabel, countyName],
  );

  return (
    <div className="grid gap-4">
      <PageHeader
        title={p.title}
        description={p.description}
        actions={
          <SearchField
            className="w-full sm:w-96 xl:w-[28rem]"
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder={p.searchPlaceholder}
          />
        }
      />
      <div className="flex items-center justify-between gap-3">
        <UnderlineTabs
          label={p.columns.status}
          value={filter}
          onChange={(value) => {
            setFilter(value);
            setPage(1);
          }}
          options={FILTERS.map((value) => ({
            value,
            label: value === 'all' ? t.common.all : t.status.provider[value],
            count: counts.data
              ? value === 'all'
                ? Object.values(counts.data).reduce((sum, n) => sum + n, 0)
                : counts.data[value]
              : undefined,
          }))}
        />
        {/* Beside the tabs, so the details start level with the list */}
        {selected && (
          <FullPageLink to={`/admin/providers/${selected}`} className="hidden shrink-0 lg:inline-flex" />
        )}
      </div>
      <MasterDetail
        divided
        open={selected !== null}
        onClose={() => setSelected(null)}
        labels={{ details: p.title }}
        placeholder={<EmptyState icon={MousePointerClick} title={t.common.pickItem} />}
        detail={
          selected ? (
            <DetailPanel fullPage={`/admin/providers/${selected}`} linkBesideTabs>
              <AdminProviderDetail id={selected} />
            </DetailPanel>
          ) : null
        }
        list={
          <>
            <QueryView query={query}>
              {(result) => (
                <>
                  <DataTable
                    layout="cards"
                    columns={columns}
                    data={result.items}
                    getRowId={(provider) => provider.id}
                    renderCard={(provider) => (
                      <SelectableCard
                        to={`/admin/providers/${provider.id}`}
                        selected={provider.id === selected}
                        onSelect={() => setSelected(provider.id)}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar
                            seed={provider.id}
                            name={provider.companyName ?? ''}
                            kind="company"
                            size="md"
                          />
                          <div className="grid min-w-0 flex-1 gap-0.5">
                            <p className="truncate font-semibold text-secondary">
                              {provider.companyName ?? p.noCompanyName}
                            </p>
                            <p className="truncate text-sm text-muted-foreground">
                              {provider.orgNumber
                                ? formatOrgNumber(provider.orgNumber)
                                : provider.contactName}
                            </p>
                          </div>
                          <StatusBadge status={provider.status} statuses={statuses.provider} />
                        </div>
                      </SelectableCard>
                    )}
                    empty={
                      search !== '' ? (
                        <EmptyState
                          icon={BadgeCheck}
                          title={t.common.noMatchesTitle}
                          description={t.common.noMatchesText}
                        />
                      ) : (
                        <EmptyState icon={BadgeCheck} title={p.empty} description={p.emptyText} />
                      )
                    }
                  />
                  <Pagination
                    page={result.page}
                    pageSize={result.pageSize}
                    total={result.total}
                    onPageChange={setPage}
                    labels={{
                      previous: t.common.previous,
                      next: t.common.next,
                      showing: (from, to, total) => fill(t.common.showingRange, { from, to, total }),
                    }}
                  />
                </>
              )}
            </QueryView>
          </>
        }
      />
    </div>
  );
}
